import React, { useCallback, useEffect, useMemo, useState } from "react";
import FavoritesTable from "../../library/FavoritesTable/FavoritesTable";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import {
  mapDispatchToProps,
  mapStateToProps,
  favoritesReducer,
} from "./FavoritesTable.store";
import CustomModal, {
  ModalSize,
} from "../../ui-library/CustomModal/CustomModal";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import FavoriteMeal from "../FavoriteMeal/FavoriteMeal";
import { useTranslation } from "react-i18next";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";

export const SmartFavoritesTable = (props) => {
  const {
    searchedParams,
    totalItems,
    favoriteMeals,
    fetchFavoriteMeals,
    canEditFavorites,
    canViewDetailFavorites,
    canCreateFavorites,
    resetState,
    loading,
    dietMenuItems,
    fetchDietDiaryMenuItems,
    deleteFavoriteMeal,
    canRemoveFavorites,
  } = props;

  const { t } = useTranslation();

  const [selectedFavoriteMeal, setSelectedFavoriteMeal] = useState(null);
  const [favoriteMealModalTitle, setFavoriteMealModalTitle] = useState("");

  const [favoriteMealToRemove, setFavoriteMealToRemove] = useState(null);

  const dietDiaryOptions = useMemo(() => {
    const items =
      dietMenuItems?.map((item, i) => ({
        id: item.menuDisplayMealItemId,
        order: i,
        defaultItems: JSON.parse(item.defaultItemsJson),
        title: `${t("appText.add")} ${dietDairyTypeToString(
          item.dietDiaryType
        )}`,
        text: dietDairyTypeToString(item.dietDiaryType),
        type: item.dietDiaryType,
      })) || [];

    items.unshift({
      id: "1",
      order: -1,
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.FOOD_DRINK
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
      type: DietDairyTypes.FOOD_DRINK,
    });

    return {
      ...items?.map((item) => ({
        ...item,
        isVisible: true,
        onClick: () => {
          setSelectedFavoriteMeal(item);
        },
      })),
    };
  }, [dietMenuItems, t]);

  useEffect(() => {
    fetchDietDiaryMenuItems();
  }, [fetchDietDiaryMenuItems]);

  const getData = useCallback(
    (params) => {
      fetchFavoriteMeals(params).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    },
    [fetchFavoriteMeals]
  );

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  useEffect(() => {
    if (totalItems === null) {
      getData(searchedParams);
    }
  }, [getData, searchedParams, totalItems]);

  const onEditFavoriteMeal = (favorite) => {
    const type = JSON.parse(favorite.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setFavoriteMealModalTitle(`${t("appText.edit")} ${favorite.name}`);

    setSelectedFavoriteMeal({
      favoriteId: favorite.favoriteId,
      jsonData: favorite.json,
      date: favorite.date,
      type: type,
      readOnly: false,
      name: favorite.name,
      reasonForChange: favorite.reasonForChange,
    });
  };

  const onViewFavoriteMeal = (favorite) => {
    const type = JSON.parse(favorite.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setFavoriteMealModalTitle(`${t("appText.view")} ${favorite.name}`);

    setSelectedFavoriteMeal({
      favoriteId: favorite.favoriteId,
      jsonData: favorite.json,
      date: favorite.date,
      type: type,
      readOnly: true,
      name: favorite.name,
      reasonForChange: favorite.reasonForChange,
    });
  };

  const onDeleteFavoriteMeal = (favorite) => {
    deleteFavoriteMeal(favorite.favoriteId)
      .then((success) => {
        setFavoriteMealToRemove(null);

        sendNotification(success, ToastType.SUCCESS);

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const onCloseFavoriteMealModal = (refresh) => {
    setSelectedFavoriteMeal(null);

    if (refresh) {
      getData(searchedParams);
    }
  };

  return (
    <>
      <CustomModal
        show={selectedFavoriteMeal !== null}
        title={favoriteMealModalTitle ?? ""}
        size={ModalSize.large}
        onClose={() => onCloseFavoriteMealModal(false)}
      >
        <FavoriteMeal
          defaultItems={selectedFavoriteMeal?.defaultItems}
          type={selectedFavoriteMeal?.type}
          onSubmit={() => onCloseFavoriteMealModal(true)}
          onClose={() => onCloseFavoriteMealModal(false)}
          readOnly={selectedFavoriteMeal?.readOnly}
          favoriteId={selectedFavoriteMeal?.favoriteId}
          mealJsonData={selectedFavoriteMeal?.jsonData}
          favoriteMeal={selectedFavoriteMeal}
          setModalTitle={setFavoriteMealModalTitle}
          existingReasonForChange={selectedFavoriteMeal?.reasonForChange}
        />
      </CustomModal>

      <CustomModal
        title={t("appText.confirmRequest.title")}
        onClose={() => setFavoriteMealToRemove(null)}
        show={!!favoriteMealToRemove}
      >
        <ConfirmRequest
          message={`${t("appText.dietDiary.remove.confirmation")}<br/>${
            favoriteMealToRemove?.entriesString
          }.`}
          warningMessage={t("appText.favorites.remove.confirmation")}
          onConfirm={() => onDeleteFavoriteMeal(favoriteMealToRemove)}
          onClose={() => setFavoriteMealToRemove(null)}
        />
      </CustomModal>

      <FavoritesTable
        {...{ getData }}
        {...{ searchedParams }}
        totalitems={totalItems}
        loading={loading}
        favorites={favoriteMeals}
        {...{ canEditFavorites }}
        {...{ canViewDetailFavorites }}
        {...{ canCreateFavorites }}
        {...{ canRemoveFavorites }}
        onEdit={onEditFavoriteMeal}
        onView={onViewFavoriteMeal}
        onDelete={setFavoriteMealToRemove}
        {...{ dietDiaryOptions }}
      />
    </>
  );
};

export default dynamic(
  "favoritesReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(favoritesReducer)
)(connect(mapStateToProps, mapDispatchToProps)(SmartFavoritesTable));
